import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners,
  defaultDropAnimation,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import styles from './KanbanView.module.css';

function KanbanCard({ id, item, renderCard, canEdit, onClick, isOverlay, density, accent }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useDraggable({
    id,
    data: { item },
    disabled: !canEdit,
  });

  const baseStyle = isOverlay
    ? {}
    : {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      };

  const style = accent
    ? { ...baseStyle, boxShadow: `inset 3px 0 0 ${accent}` }
    : baseStyle;

  const handlePointerUp = (e) => {
    if (e.button !== 0) return;
    if (!onClick) return;
    onClick(item);
  };

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      style={style}
      className={[
        styles.card,
        canEdit ? styles.cardDraggable : '',
        isOverlay ? styles.cardOverlay : '',
        density === 'compact' ? styles.cardCompact : '',
      ].filter(Boolean).join(' ')}
      {...(isOverlay ? {} : attributes)}
      {...(isOverlay ? {} : listeners)}
      onClick={isOverlay ? undefined : handlePointerUp}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : -1}
    >
      {renderCard(item)}
    </div>
  );
}

function Column({ col, items, renderCard, canEdit, onCardClick, getCardAccent, density, activeColumnId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${col.id}`,
    data: { columnId: col.id },
  });

  const isActiveTarget = isOver && activeColumnId !== col.id;

  return (
    <div
      ref={setNodeRef}
      className={`${styles.column} ${isActiveTarget ? styles.columnOver : ''}`}
    >
      <div className={styles.columnHeader}>
        <div className={styles.columnHeaderLeft}>
          <span className={styles.columnDot} style={{ background: col.color || '#6b7280' }} />
          <span className={styles.columnName} title={col.name}>{col.name}</span>
          <span className={styles.columnCount}>{items.length}</span>
        </div>
      </div>

      <div className={styles.cardList}>
        {items.map((item) => {
          const itemKey = item.project_id ?? item.lead_id ?? item.id;
          const accent = getCardAccent ? getCardAccent(item) : null;
          return (
            <KanbanCard
              key={itemKey}
              id={`card-${itemKey}`}
              item={item}
              renderCard={renderCard}
              canEdit={canEdit}
              onClick={onCardClick}
              density={density}
              accent={accent}
            />
          );
        })}
        {items.length === 0 && (
          <div className={`${styles.emptyColumn} ${isActiveTarget ? styles.emptyColumnOver : ''}`}>
            {isActiveTarget ? 'Drop here' : 'No items'}
          </div>
        )}
        {isActiveTarget && items.length > 0 && (
          <div className={styles.dropIndicator} aria-hidden="true" />
        )}
      </div>
    </div>
  );
}

export default function KanbanView({
  columns,
  items,
  getColumnId,
  renderCard,
  onCardMove,
  onCardClick,
  canEdit,
  density = 'comfortable',
  getCardAccent,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const [activeId, setActiveId] = useState(null);
  const itemsByIdRef = useRef(new Map());

  useEffect(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.project_id ?? item.lead_id ?? item.id;
      map.set(`card-${key}`, item);
    });
    itemsByIdRef.current = map;
  }, [items]);

  const groupedItems = useMemo(() => {
    const groups = new Map();
    columns.forEach((col) => groups.set(String(col.id), []));
    items.forEach((item) => {
      const colId = String(getColumnId(item));
      if (groups.has(colId)) groups.get(colId).push(item);
    });
    return groups;
  }, [columns, items, getColumnId]);

  const activeItem = activeId ? itemsByIdRef.current.get(activeId) : null;

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const item = active.data?.current?.item;
    const targetColumnId = over.data?.current?.columnId;
    if (!item || targetColumnId == null) return;

    const currentColumnId = String(getColumnId(item));
    if (String(targetColumnId) === currentColumnId) return;

    onCardMove?.(item, targetColumnId);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className={styles.board}>
        {columns.map((col) => (
          <Column
            key={col.id}
            col={col}
            items={groupedItems.get(String(col.id)) || []}
            renderCard={renderCard}
            canEdit={canEdit}
            onCardClick={onCardClick}
            getCardAccent={getCardAccent}
            density={density}
            activeColumnId={activeItem ? String(getColumnId(activeItem)) : null}
          />
        ))}
      </div>

      {typeof document !== 'undefined' && createPortal(
        <DragOverlay dropAnimation={defaultDropAnimation}>
          {activeItem ? (
            <KanbanCard
              id="overlay"
              item={activeItem}
              renderCard={renderCard}
              canEdit={canEdit}
              isOverlay
              density={density}
              accent={getCardAccent ? getCardAccent(activeItem) : null}
            />
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}
